/**
 * Salience-section sidebar — a markets "watchlist" of tracked terms. Replaces
 * the YouTube video list when in /salience/*. Each row shows the term's
 * headline attention + velocity (green/red), and links to its detail page.
 */
import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { apiSalience } from "../lib/api";
import { SectionNav } from "./SectionNav";
import {
  DIRECTION_ARROW,
  DIRECTION_CLASS,
  direction,
  formatHeadlineValue,
  formatHeadlineVelocity,
  termHeadline,
  type Headline,
} from "../components/salience/format";

const KIND_ORDER: Record<string, number> = { topic: 0, tagline: 1, entity: 2 };
const KIND_LABELS: Record<string, string> = {
  topic: "Topic",
  tagline: "Tagline",
  entity: "Entity",
};

interface Props {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface Row {
  termId: string;
  label: string;
  kind: string;
  headline: Headline | null;
}

export function SalienceSidebar({ mobileOpen, onMobileClose }: Props) {
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["salience", "overview"],
    queryFn: apiSalience.overview,
    retry: (n, err) =>
      !(err instanceof Error && err.message.includes("404")) && n < 1,
  });
  const termsQ = useQuery({
    queryKey: ["salience", "terms"],
    queryFn: () => apiSalience.terms(),
  });

  const terms = data?.terms ?? {};
  // Full active registry — every monitored term, headline where it has metrics.
  const rows = useMemo<Row[]>(() => {
    const q = search.trim().toLowerCase();
    return (termsQ.data ?? [])
      .filter((t) => !q || t.label.toLowerCase().includes(q))
      .map((t) => ({
        termId: t.term_id,
        label: t.label,
        kind: t.kind,
        headline: terms[t.term_id] ? termHeadline(terms[t.term_id]) : null,
      }))
      .sort((a, b) => {
        if (!!a.headline !== !!b.headline) return a.headline ? -1 : 1;
        const ka = KIND_ORDER[a.kind] ?? 9;
        const kb = KIND_ORDER[b.kind] ?? 9;
        if (ka !== kb) return ka - kb;
        if (a.headline && b.headline)
          return (b.headline.value ?? -1) - (a.headline.value ?? -1);
        return a.label.localeCompare(b.label);
      });
  }, [termsQ.data, terms, search]);

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 top-14 z-20 bg-black/40 md:hidden"
          onClick={onMobileClose}
          aria-hidden
        />
      )}

      <aside
        className={`
          fixed top-14 bottom-0 left-0 z-30 flex w-72 flex-col overflow-hidden border-r border-rule bg-surface
          transition-transform duration-200 ease-out
          md:static md:z-auto md:w-auto md:translate-x-0
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="flex items-start justify-between border-b border-rule px-4 py-3">
          <div className="flex-1">
            {/* Mobile only — top-level module switcher, folded into the burger
                drawer (hidden on md+ where the topbar carries it). */}
            <div className="-mx-1 mb-3 border-b border-rule pb-3 md:hidden">
              <SectionNav orientation="vertical" onNavigate={onMobileClose} />
            </div>

            <div className="mb-3 flex flex-wrap gap-1 text-[12px] font-medium">
              <PageNavLink to="/salience" label="Overview" />
              <PageNavLink to="/salience/methods" label="Methods" />
              <PageNavLink to="/salience/eval" label="Coder eval" />
            </div>

            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
              Watchlist
            </div>

            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
              />
              <input
                type="search"
                placeholder="Filter terms…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 text-sm text-ink placeholder:text-ink-muted focus:border-brand-indigo focus:bg-surface focus:outline-none"
              />
            </div>
          </div>

          <button
            onClick={onMobileClose}
            className="ml-2 rounded p-1 text-ink-muted hover:bg-black/5 hover:text-ink md:hidden"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="space-y-1 p-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-11 animate-pulse rounded bg-black/5" />
              ))}
            </div>
          )}
          {!isLoading && (
            <ul>
              {rows.map((r) => (
                <WatchRow key={r.termId} row={r} />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}

function WatchRow({ row }: { row: Row }) {
  const h = row.headline;
  const dir = direction(h?.velocity);
  const quiet = h === null;
  return (
    <li>
      <NavLink
        to={`/salience/terms/${row.termId}`}
        className={({ isActive }) =>
          `block border-l-2 px-4 py-2.5 transition-colors ${
            isActive
              ? "border-brand-indigo bg-black/5"
              : "border-transparent hover:bg-black/3"
          }`
        }
      >
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={`truncate text-sm ${quiet ? "text-ink-muted" : "text-ink"}`}
            title={row.label}
          >
            {row.label}
          </span>
          <span
            className={`font-mono text-sm font-semibold tabular-nums ${
              quiet ? "text-ink-muted/50" : "text-ink"
            }`}
          >
            {h ? formatHeadlineValue(h) : "—"}
          </span>
        </div>
        <div className="mt-0.5 flex items-baseline justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-ink-muted">
            {KIND_LABELS[row.kind] ?? row.kind}
          </span>
          {h ? (
            <span
              className={`font-mono text-[11px] font-semibold tabular-nums ${DIRECTION_CLASS[dir]}`}
            >
              {DIRECTION_ARROW[dir]} {formatHeadlineVelocity(h)}
            </span>
          ) : (
            <span className="text-[10px] text-ink-muted/60">no signal yet</span>
          )}
        </div>
      </NavLink>
    </li>
  );
}

function PageNavLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `rounded px-2 py-1 transition-colors ${
          isActive
            ? "bg-brand-indigo/10 text-brand-indigo"
            : "text-ink-muted hover:bg-black/4 hover:text-ink"
        }`
      }
    >
      {label}
    </NavLink>
  );
}
